import * as React from "react";
import { createAlchemyWeb3 } from "@alch/alchemy-web3";
import { useQuery } from "react-query";
import BigNumber from "bignumber.js";
import { constants } from "config";
import type { Provider } from "features/auth/types";
import {
  api,
  ApiError,
  getMintApproval,
  getMintPermitted,
  GetMintPermittedResponse,
} from "utils/api";
import { useAsync } from "utils/hooks";
import contractAbi from "../contracts/HUGMembershipPass.json";

const web3 = createAlchemyWeb3(constants.alchemyUrl as string);

const contract = new web3.eth.Contract(
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  contractAbi as any,
  constants.contractAddress
);

const bigNumberToInteger = (b: BigNumber) => parseInt(b.toString(), 10);

const useMintHelpers = (wallet: string | undefined) => {
  const { run: mintsUsedRun, ...mintsUsed } = useAsync<number, ApiError>();
  const { run: mintPermittedRun, ...mintPermitted } =
    useAsync<GetMintPermittedResponse>();
  const [mintProcessing, setMintProcessing] = React.useState<boolean>(false);
  const [mintDone, setMintDone] = React.useState<boolean>(false);
  const [mintTokenIds, setMintTokenIds] = React.useState<string[]>([]);
  const [mintTxHash, setMintTxHash] = React.useState<string | undefined>(
    undefined
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mintError, setMintError] = React.useState<any>(undefined);

  //
  // When wallet is connected - Get Mint Count & Mint Permission
  //
  React.useEffect(() => {
    if (wallet) {
      // Call Contract - How many mints used
      mintsUsedRun(
        contract.methods
          .hugsMinted(wallet)
          .call()
          .then((mintsUsedResponse: BigNumber) =>
            bigNumberToInteger(mintsUsedResponse)
          )
      );

      // Call API - Is Wallet Approved
      mintPermittedRun(getMintPermitted(wallet));
    }
  }, [wallet, mintPermittedRun, mintsUsedRun]);

  //
  // How much will the mint cost?
  //
  const calculateMintCost = (
    count: number,
    alreadyMinted: number,
    oneFree: boolean
  ) => {
    if (alreadyMinted === 0 && oneFree) {
      return (count - 1) * 0.06;
    }
    return count * 0.06;
  };

  //
  // Callbacks for mint
  //
  const mintStarted = React.useCallback(
    (txHash: string) => {
      setMintTxHash(txHash);
    },
    [setMintTxHash]
  );

  const mintFinished = React.useCallback(
    (tokenIds: string[]) => {
      setMintTokenIds(tokenIds);
      setMintDone(true);
      setMintProcessing(false);
    },
    [setMintTokenIds, setMintDone, setMintProcessing]
  );

  //
  // Check Status - On 5 Second Timer
  //
  const checkMintStatus = async (txHash: string) => {
    const tx = await web3.eth.getTransactionReceipt(txHash);
    if (tx) {
      const tokenIds: string[] = tx.logs.map((log) => {
        const hexId = log.topics[3];
        const tokenId = web3.utils.hexToNumber(hexId);
        return tokenId.toString();
      });
      mintFinished(tokenIds);
    } else {
      setTimeout(() => {
        checkMintStatus(txHash);
      }, 5000);
    }
  };

  const mint = async (
    walletProvider: Provider,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transactionParameters: any
  ) => {
    // Execute Mint
    try {
      const txHash = (await walletProvider.request({
        method: "eth_sendTransaction",
        params: [transactionParameters],
      })) as string;
      mintStarted(txHash);

      setTimeout(() => {
        checkMintStatus(txHash);
      }, 5000);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error2: any) {
      // eslint-disable-next-line no-console
      console.error("error", error2);
      setMintError(error2);
    }
  };

  //
  // Mint an NFT
  //
  const mintNft = async (
    walletProvider: Provider | undefined,
    walletAddress: string,
    count: number
  ) => {
    // Reset
    setMintProcessing(true);
    setMintDone(false);
    setMintTokenIds([]);
    setMintError(undefined);
    setMintTxHash(undefined);

    if (wallet && typeof mintsUsed !== "undefined") {
      // Get Approval
      const { isCurator, oneFree, signature } = await getMintApproval(wallet);

      // Price only needed for minting two
      const cost = calculateMintCost(count, mintsUsed.data as number, oneFree);
      const wei = web3.utils.toWei(cost.toString(), "ether");

      // Mint Params
      const mintCall = contract.methods
        .mintHUG(count, signature, isCurator, oneFree)
        .encodeABI();
      const transactionParameters = {
        to: constants.contractAddress,
        from: walletAddress,
        value: web3.utils.toHex(wei),
        data: mintCall,
      };

      if (walletProvider) {
        mint(walletProvider, transactionParameters);
      }
    }
  };

  //
  // Calculations
  //
  const mintsMax =
    mintPermitted.isSuccess && mintPermitted.data?.isCurator ? 4 : 3;

  return {
    mintsUsed,
    mintPermitted,
    mintsMax,
    isCurator: mintPermitted.data?.isCurator,
    isAdmin: mintPermitted.data?.isAdmin,
    isLoading: mintsUsed.isLoading || mintPermitted.isLoading,
    mintNft,
    mintProcessing,
    mintDone,
    mintTokenIds,
    mintError,
    mintTxHash,
    calculateMintCost,
  };
};

const useMintEnabled = () => {
  const { data, ...query } = useQuery("mintEnabled", () => api(""));
  return {
    mintEnabled: data?.mintEnabled,
    ...query,
  };
};

export { useMintEnabled, useMintHelpers, web3 };
